import {
  type PaginationModel,
  type SortModel,
  Table,
  type TableColumns
} from '@/components'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { PermissionGuard } from '@/components/PermissionGuard.tsx'
import { SiteAssignmentPopover } from '@/features/user/components'
import {
  useAssignSite,
  useResendActivation,
  useUnassignSite
} from '@/features/user/hooks'
import { useEmployeeUserList } from '@/features/user/hooks/useEmployeeUserList.ts'
import EmailIcon from '@mui/icons-material/Email'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { createFileRoute, createLink, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import type { UserEmployee } from '@/features/user/api/user-employee.data.ts'
import { useCompany } from '@/contexts'

export const Route = createFileRoute('/_authenticated/$companySlug/users-old/')({
  beforeLoad: ({ context }) => {
    // Check if user management feature is enabled
    if (!isFeatureEnabled(FEATURES.USER_MANAGEMENT)) {
      throw redirect({
        to: '/not-found'
      })
    }
    context.auth.rejectIfMissingPermissions(['USER_READ'])
  },
  component: UserOverview
})

const LinkButton = createLink(Button)

function UserOverview() {
  const { t } = useTranslation(['_routes', '_global'])
  const { sites } = useCompany()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortModel, setSortModel] = useState<SortModel<UserEmployee>>()
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 10
  })

  const users = useEmployeeUserList({
    search: searchQuery,
    sortModel,
    paginationModel
  })

  // Grab all fleets for the users on the page
  const assignFleet = useAssignSite()
  const unassignFleet = useUnassignSite()
  const resendActivation = useResendActivation()

  const [popoverState, setPopoverState] = useState<{
    userId: string
    anchorEl: HTMLElement | null
    assignedSiteIds: string[] | null
  } | null>(null)

  const handleClosePopover = () => {
    setPopoverState(null)
  }

  const handleDelete = (userId: string, siteId: string) => {
    return unassignFleet.mutateAsync({ userId, siteId })
  }

  const handleAddFleet = (
    event: React.MouseEvent<HTMLButtonElement>,
    userId: string,
    assignedSiteIds: string[] | null
  ) => {
    setPopoverState({
      userId,
      anchorEl: event.currentTarget,
      assignedSiteIds
    })
  }

  const handleResendActivation = (userId: string) => {
    resendActivation.mutate({ userId })
  }

  const columns: TableColumns<UserEmployee> = {
    firstName: {
      label: t('routes.users.main.table.columns.firstName'),
      width: '10%'
    },
    lastName: {
      label: t('routes.users.main.table.columns.lastName'),
      width: '10%'
    },
    email: {
      label: t('routes.users.main.table.columns.email'),
      width: '10%',
      render: value => <Typography>{value}</Typography>
    },
    phoneNumber: {
      label: t('routes.users.main.table.columns.phoneNumber'),
      width: '10%'
    },
    isActivated: {
      label: t('routes.users.main.table.columns.status'),
      width: '10%',
      render: (value, row) => (
        <Stack direction="row" gap={1} alignItems="center">
          <Chip
            label={
              value
                ? t('routes.users.main.status.active')
                : t('routes.users.main.status.pendingActivation')
            }
            color={value ? 'success' : 'warning'}
            size="small"
          />
          {!value && (
            <Tooltip title={t('routes.users.main.buttons.resendActivation')}>
              <IconButton
                size="small"
                onClick={() => handleResendActivation(row.id)}
                disabled={resendActivation.isPending}
                color="primary"
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    },
    restrictedToSites: {
      label: t('routes.users.main.table.columns.assignedSites'),
      width: '35%',
      render: (value, row) => (
        <Stack direction="row" gap={1}>
          {value &&
            value.map(it => {
              const fleetName = sites.find(site => site.id == it)?.name

              return (
                <Chip
                  key={it}
                  label={fleetName || <LinearProgress sx={{ width: 40 }} />}
                  onDelete={() => handleDelete(row.id, it)}
                  disabled={unassignFleet.isPending}
                />
              )
            })}
          <IconButton
            aria-label="add"
            size="small"
            onClick={event => handleAddFleet(event, row.id, value ?? [])}
          >
            <AddIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      )
    }
  }

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <PageHeader>{t('routes.users.main.title')}</PageHeader>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <PermissionGuard permissions={['USER_WRITE', 'USER_READ']}>
            <LinkButton
              variant="contained"
              to="/$companySlug/users/create"
              from={Route.fullPath}
            >
              {t('routes.users.main.buttons.creation')}
            </LinkButton>
          </PermissionGuard>

          <TextField
            variant="outlined"
            placeholder={t('routes.users.main.searchBar.user')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>

        {users.error && (
          <Alert severity="error">
            {t('routes.users.main.errors.gettingUser')}:{' '}
            {t(`api.message.errors.${users.error.message}`, {
              defaultValue: users.error.cause as string
            })}
          </Alert>
        )}
        {resendActivation.isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('routes.users.main.messages.activationEmailSent')}
          </Alert>
        )}
        {resendActivation.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('routes.users.main.errors.resendActivation')}:{' '}
            {resendActivation.error.message}
          </Alert>
        )}

        <Box sx={{ mt: 4 }}>
          <Table
            columns={columns}
            data={users.data?.content || []}
            keyExtractor={row => row.id}
            totalItems={users.data?.totalItems}
            isLoading={users.isLoading}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </Box>
      </Paper>

      {popoverState && (
        <SiteAssignmentPopover
          userId={popoverState.userId}
          assignedSiteIds={popoverState.assignedSiteIds}
          assignSite={(userId, siteId) => assignFleet.mutateAsync({ userId, siteId })}
          anchorEl={popoverState.anchorEl}
          open={true}
          onClose={handleClosePopover}
        />
      )}
    </>
  )
}
